import { DOCUMENT, Injectable, LOCALE_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@awo-care/shared-auth';

/** Locales the website is built for (see project.json i18n config). */
const LOCALES = ['de', 'en'] as const;
type Locale = (typeof LOCALES)[number];

const DEFAULT_LOCALE: Locale = 'de';
const BRAND = 'AWO Care';

export interface SeoMetadata {
  /** Page title without the brand suffix. Empty for the home page. */
  title?: string;
  /** Meta description for this page. Falls back to a generic one. */
  description?: string;
  /** Overrides the canonical path — used by routes that alias another page. */
  canonicalPath?: string;
  /** Keeps the page out of search indexes (404, deep-link handlers). */
  noindex?: boolean;
}

/**
 * Sets title, description, canonical and hreflang tags per route.
 *
 * Runs during prerendering as well as in the browser, so it must not touch
 * browser-only globals — the injected DOCUMENT works in both.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly locale = normalizeLocale(inject(LOCALE_ID));

  private readonly baseUrl = environment.appUrls.website.replace(/\/$/, '');

  update(metadata: SeoMetadata, url: string): void {
    const path = normalizePath(url);
    const canonicalPath = metadata.canonicalPath ?? path;

    this.title.setTitle(
      metadata.title ? `${metadata.title} | ${BRAND}` : BRAND,
    );

    // Always set the description: otherwise a page without its own text would
    // keep the description of the previously visited route.
    this.meta.updateTag({
      name: 'description',
      content: metadata.description ?? defaultDescription(),
    });

    if (metadata.noindex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
    } else {
      this.meta.removeTag('name="robots"');
    }

    this.setCanonical(this.absoluteUrl(this.locale, canonicalPath));
    this.setAlternates(canonicalPath);
  }

  /** Absolute, locale-prefixed URL with trailing slash (avoids a 301 hop). */
  private absoluteUrl(locale: Locale, path: string): string {
    return `${this.baseUrl}/${locale}${path}`;
  }

  private setCanonical(href: string): void {
    const link =
      this.document.head.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      ) ?? this.appendLink({ rel: 'canonical' });

    link.setAttribute('href', href);
  }

  private setAlternates(path: string): void {
    for (const hreflang of [...LOCALES, 'x-default']) {
      const locale = hreflang === 'x-default' ? DEFAULT_LOCALE : hreflang;
      const link =
        this.document.head.querySelector<HTMLLinkElement>(
          `link[rel="alternate"][hreflang="${hreflang}"]`,
        ) ?? this.appendLink({ rel: 'alternate', hreflang });

      link.setAttribute('href', this.absoluteUrl(locale as Locale, path));
    }
  }

  private appendLink(attributes: Record<string, string>): HTMLLinkElement {
    const link = this.document.createElement('link');
    for (const [name, value] of Object.entries(attributes)) {
      link.setAttribute(name, value);
    }
    this.document.head.appendChild(link);
    return link;
  }
}

/** Mirrors the description in index.html for routes without their own text. */
function defaultDescription(): string {
  return $localize`:@@seo.default.description:AWO Care – Serviceportal für die Betreuung von Kindern an hessischen Schulen: Anmeldung, Ferienbetreuung und Verträge online verwalten.`;
}

/**
 * Strips query string and fragment and enforces a trailing slash, so that the
 * canonical URL matches what Firebase Hosting actually serves.
 */
function normalizePath(url: string): string {
  const path = url.split(/[?#]/)[0];
  if (path === '' || path === '/') {
    return '/';
  }
  return path.endsWith('/') ? path : `${path}/`;
}

/** LOCALE_ID is 'de'/'en' in localized builds, but e.g. 'en-US' without i18n. */
function normalizeLocale(localeId: string): Locale {
  const language = localeId.split('-')[0];
  return LOCALES.includes(language as Locale)
    ? (language as Locale)
    : DEFAULT_LOCALE;
}
